<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      persistent
      width="1024"
      @click:outside="dialog = false"
    >
      <template #activator="{ props }">
        <v-btn
          :color="this.color"
          append-icon="mdi-pencil-outline"
          v-bind="props"
          :class="this.text ? '' : 'button'"
          variant="text"
        >
          {{ this.text }}
        </v-btn>
      </template>

      <v-card>
        <v-card-title>
          <div class="d-flex justify-space-between">
            <span class="text-h5">Editar Charter {{ this.identificacao }}</span>
            <v-btn
              variant="plain"
              rounded="xl"
              prepend-icon="mdi-window-close"
              @click="dialog = false"
            ></v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-form ref="form" validate-on="lazy submit">
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="this.objetivo"
                    required
                    label="Objetivo*"
                    rows="2"
                    :rules="objetivoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="this.pre_condicao"
                    required
                    label="Pré-Condição*"
                    rows="3"
                    :rules="preCondicaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="this.observacao"
                    required
                    label="Observação*"
                    rows="3"
                    :rules="observacaoRules"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" sm="6" md="6">
                  <v-select
                    v-model="this.statusCharter"
                    label="Status*"
                    required
                    :items="statusesCharter"
                    :rules="statusSelecionadoRules"
                  ></v-select>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
          <small>*Campo obrigatório</small>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="blue-darken-1" variant="text" @click="submitForm">
            Atualizar
            <v-progress-circular
              v-if="this.isLoading"
              class="ml-2"
              indeterminate
              size="20"
            />
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script>
/**
 * @module EditCharter
 * @desc Modal com formulário para a edição de um charter.
 * @vue-data {boolean} [dialog=false] - Flag que indica se o modal está visível.
 * @vue-data {string} [identificacao=''] - Identificação do charter.
 * @vue-data {string} [objetivo=''] - Objetivo do charter.
 * @vue-data {string} [pre_condicao=''] - Pré-condição do charter.
 * @vue-data {string} [observacao=''] - Observação para o charter.
 * @vue-data {string} [statusCharter=''] - Status do charter.
 * @vue-data {Array.<String>} [statusesCharter=''] - Array com os possíveis
 * status do charter.
 * @vue-data {boolean} [isLoading=false] - Flag que indica se o charter ainda
 * está sendo editado.
 */
import axios from 'axios';

import store from '@/store/store';

const apiUrl = process.env.VUE_APP_API_URL;

export default {
  props: {
    id: Number,
    charter: Object,
    text: {
      type: String,
      default: 'Editar',
    },
    color: {
      type: String,
      default: 'orange',
    },
  },
  emits: ['charterEditado'],
  data: () => ({
    dialog: false,
    identificacao: '',
    objetivo: '',
    // eslint-disable-next-line camelcase
    pre_condicao: '',
    observacao: '',
    statusCharter: '',
    statusesCharter: ['PLANEJADO', 'BLOQUEADO', 'EXECUTADO'],
    isLoading: false,
    objetivoRules: [
      (v) => !!v || 'Objetivo é obrigatório',
      (v) =>
        (v && v.length <= 200) || 'Objetivo deve ter no máximo 200 caracteres',
    ],
    preCondicaoRules: [
      (v) => !!v || 'Pré-condição é obrigatória',
      (v) =>
        (v && v.length <= 400) ||
        'Pré-condição deve ter no máximo 400 caracteres',
    ],
    observacaoRules: [
      (v) => !!v || 'Observação é obrigatória',
      (v) =>
        (v && v.length <= 500) ||
        'Observação deve ter no máximo 500 caracteres',
    ],
    statusSelecionadoRules: [(v) => !!v || 'Status é obrigatório'],
  }),
  mounted() {
    console.log('id', this.id);
    this.identificacao = this.charter.identificacao;
    this.objetivo = this.charter.objetivo;
    // eslint-disable-next-line camelcase
    this.pre_condicao = this.charter.pre_condicao;
    this.observacao = this.charter.observacao;
    this.statusCharter = this.charter.status_charter;
    window.addEventListener('keydown', this.handleKeyDown);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  },
  methods: {
    /**
     * Lida com o evento de pressionar tecla. Se a tecla pressionada for ESC,
     * o modal é fechado.
     *
     * @param {Object} event Objeto do evento
     */
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.dialog = false;
      }
    },

    /**
     * Realiza uma requisição HTTP para obter informações sobre um
     * determinado charter.
     *
     * @param {Number} id O id do charter.
     */
    async fetchCharter(id) {
      try {
        const response = await axios.get(`${apiUrl}/api/v1/charters/${id}`, {
          headers: {
            Authorization: `Bearer ${store.state.token}`,
          },
        });

        this.identificacao = response.data.charter.identificacao;
        this.objetivo = response.data.charter.objetivo;
        // eslint-disable-next-line camelcase
        this.pre_condicao = response.data.charter.pre_condicao;
        this.observacao = response.data.charter.observacao;
        // eslint-disable-next-line camelcase
        this.statusCharter = response.data.charter.status_charter;
      } catch (error) {
        console.log(error);
      }
    },

    /**
     * Realiza uma requisição HTTP para atualizar um charter.
     */
    async submitForm() {
      const isValid = (await this.$refs.form.validate()).valid;

      if (isValid) {
        const data = {
          identificacao: this.identificacao,
          objetivo: this.objetivo,
          // eslint-disable-next-line camelcase
          pre_condicao: this.pre_condicao,
          observacao: this.observacao,
          // eslint-disable-next-line camelcase
          status_charter: this.statusCharter,
        };

        try {
          this.isLoading = true;
          const response = await axios.put(
            `${apiUrl}/api/v1/charters/${this.id}`,
            data,
            {
              headers: {
                Authorization: `Bearer ${store.state.token}`,
              },
            },
          );

          const projectID = this.$route.params.idProjeto;

          this.$toast.success(response.data.msg);
          this.$emit('charterEditado', this.id);

          if (this.text) {
            this.$router.push(`/projects/${projectID}/Charters/${this.id}`);
          }
        } catch (error) {
          console.log(error);
          this.$toast.error(error.response.data.msg);
        } finally {
          this.isLoading = false;
          this.dialog = false;
        }
      }
    },
  },
};
</script>
<style scoped>
.button {
  min-width: 0;
  width: 0;
  padding: 0px;
}
</style>
